// SubscriptionPage.tsx
import { Check, CheckCircle, Loader2, XCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ROUTES from '@/ROUTES'

import { PaywallMode } from '@/MODELS/appSettings.model'

import { api } from '@/services/api.service'

import logo from '../../logo-white-no-bg.svg'

type CallbackStatus = 'none' | 'success' | 'cancelled' | 'error'

export default function SubscriptionPage() {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const [callbackStatus, setCallbackStatus] = useState<CallbackStatus>('none')

    const paywallMode: PaywallMode = (searchParams.get('mode') as PaywallMode) || 'trial'

    useEffect(() => {
        const status = searchParams.get('status')
        if (status === 'success') {
            navigate('/')
        } else if (status === 'cancelled') {
            setCallbackStatus('cancelled')
        } else if (status === 'error') {
            setCallbackStatus('error')
        }
    }, [searchParams, navigate])

    if (callbackStatus === 'success') {
        return (
            <div className='flex flex-col min-h-screen p-4 my-6 overflow-y-scroll bg-gradient-to-br from-cyan-600 via-cyan-500 to-cyan-600'>
                <div className='max-w-md p-8 mx-auto text-center border bg-white/10 backdrop-blur-sm rounded-3xl border-white/30'>
                    <div className='flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-green-400 rounded-full'>
                        <CheckCircle className='w-8 h-8 text-white' />
                    </div>
                    <h2 className='mb-4 text-2xl font-bold text-white'>Welcome to BrightPath!</h2>
                    <p className='mb-6 text-white/90'>
                        Your subscription is active. Redirecting you to your dashboard...
                    </p>
                    <div className='flex items-center justify-center space-x-2 text-white/80'>
                        <Loader2 className='w-4 h-4 animate-spin' />
                        <span className='text-sm'>Taking you to your Dashboard...</span>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className='relative flex flex-col min-h-screen px-8 overflow-y-scroll bg-gradient-to-br from-cyan-600 via-cyan-500 to-cyan-600'>
            <header className='relative z-10 flex-shrink-0 px-4 py-8'>
                <div className='text-center'>
                    <img src={logo} className='h-12 mx-auto sm:h-16 lg:h-20' alt='BrightPath' />
                </div>
            </header>

            <div className='max-w-4xl py-8 mx-auto'>
                {callbackStatus === 'cancelled' && (
                    <div className='p-4 mb-6 border bg-yellow-400/20 border-yellow-400/30 rounded-xl backdrop-blur-sm'>
                        <div className='flex items-center space-x-3'>
                            <XCircle className='flex-shrink-0 w-6 h-6 text-yellow-400' />
                            <div>
                                <h3 className='font-semibold text-white'>Payment Cancelled</h3>
                                <p className='text-sm text-white/90'>
                                    {paywallMode === 'usage'
                                        ? "No worries! You can continue using BrightPath and subscribe whenever you're ready."
                                        : "No worries! You can try again whenever you're ready."
                                    }
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {callbackStatus === 'error' && (
                    <div className='p-4 mb-6 border bg-red-400/20 border-red-400/30 rounded-xl backdrop-blur-sm'>
                        <div className='flex items-center space-x-3'>
                            <XCircle className='flex-shrink-0 w-6 h-6 text-red-400' />
                            <div>
                                <h3 className='font-semibold text-white'>Something went wrong</h3>
                                <p className='text-sm text-white/90'>
                                    There was an issue processing your payment. Please try again.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <UserSubscription paywallMode={paywallMode} />
            </div>
        </div>
    )
}

function UserSubscription({ paywallMode }: { paywallMode: PaywallMode }) {
    const [plan, setPlan] = useState<string>('monthly')
    const [isLoading, setIsLoading] = useState(false)
    const navigate = useNavigate()

    const redirectToPaymentGateway = () => {
        setIsLoading(true)
        api<{ url: string }>(ROUTES.stripe.createCheckoutSession + '/' + plan)
            .then((x) => window.location.replace(x.data.url))
            .catch(() => {
                // Session may have expired — navigate home where the paywall can handle it
                navigate('/')
            })
            .finally(() => setIsLoading(false))
    }

    if (paywallMode === 'usage') {
        return <UsageSubscriptionView
            plan={plan}
            setPlan={setPlan}
            isLoading={isLoading}
            isTestMode={false}
            redirectToPaymentGateway={redirectToPaymentGateway}
            navigate={navigate}
        />
    }

    return <TrialSubscriptionView
        plan={plan}
        setPlan={setPlan}
        isLoading={isLoading}
        trialPeriodDays={30}
        isTestMode={false}
        redirectToPaymentGateway={redirectToPaymentGateway}
    />
}

// Trial-based subscription page (existing design)
function TrialSubscriptionView({
    plan, setPlan, isLoading, trialPeriodDays, isTestMode, redirectToPaymentGateway,
}: {
    plan: string
    setPlan: (p: string) => void
    isLoading: boolean
    trialPeriodDays: number
    isTestMode: boolean
    redirectToPaymentGateway: () => void
}) {
    return (
        <section className='space-y-6' key='step-subscription'>
            <div className='text-center'>
                <h2 className='mb-3 text-2xl font-bold text-white sm:text-3xl drop-shadow-lg'>
                    Choose Your BrightPath Plan
                </h2>
                <p className='text-base text-white/90 drop-shadow-md'>
                    Start your personalized autism care journey with full access to all features
                </p>
            </div>

            <div className='space-y-4'>
                <div className='grid gap-4 sm:grid-cols-2'>
                    <button
                        type='button'
                        onClick={() => setPlan('monthly')}
                        className={`p-6 rounded-2xl border transition-all duration-200 text-left ${
                            plan === 'monthly'
                                ? 'bg-white text-cyan-600 border-white shadow-md '
                                : 'bg-white/15 text-white border-white/40 hover:bg-white/25 '
                        }`}
                    >
                        <div className='space-y-3'>
                            <div className='flex items-center justify-between'>
                                <h3 className='text-xl font-bold'>Monthly</h3>
                                {plan === 'monthly' && (
                                    <div className='flex items-center justify-center w-5 h-5 rounded-full bg-cyan-600'>
                                        <Check className='w-3 h-3 text-white' />
                                    </div>
                                )}
                            </div>
                            <div className='space-y-1 text-start'>
                                <div className='text-4xl font-extrabold '>$4.99</div>
                                <div className='text-lg line-through opacity-75'>$9.99</div>
                                <div className='text-sm opacity-75'>per month</div>
                            </div>
                            {trialPeriodDays > 0 && (
                                <div className='px-3 py-2 border rounded-lg bg-gradient-to-r from-yellow-100/20 to-orange-400/20 border-yellow-100/30'>
                                    <p className='text-sm font-medium'>✨ {trialPeriodDays}-day free trial included (no card required)</p>
                                </div>
                            )}
                        </div>
                    </button>

                    <button
                        type='button'
                        onClick={() => setPlan('annual')}
                        className={`p-6 rounded-2xl border transition-all duration-200 text-left relative ${
                            plan === 'annual'
                                ? 'bg-white text-cyan-600 border-white shadow-md'
                                : 'bg-white/15 text-white border-white/40 hover:bg-white/25'
                        }`}
                    >
                        <div className='space-y-3'>
                            <div className='flex items-center justify-between'>
                                <h3 className='text-xl font-bold'>Annual</h3>
                                {plan === 'annual' && (
                                    <div className='flex items-center justify-center w-5 h-5 rounded-full bg-cyan-600'>
                                        <Check className='w-3 h-3 text-white' />
                                    </div>
                                )}
                            </div>
                            <div className='space-y-1'>
                                <div className='flex items-baseline space-x-2'>
                                    <div className='space-y-1 text-start'>
                                        <div className='text-4xl font-extrabold '>$49</div>
                                        <div className='text-lg line-through opacity-75'>$99</div>
                                    </div>
                                </div>
                                <div className='text-sm opacity-75'>per year</div>
                            </div>
                            {trialPeriodDays > 0 && (
                                <div className='px-3 py-2 border rounded-lg bg-gradient-to-r from-yellow-400/20 to-orange-400/20 border-yellow-400/30'>
                                    <p className='text-sm font-medium'>✨ {trialPeriodDays}-day free trial included (no card required)</p>
                                </div>
                            )}
                        </div>
                    </button>
                </div>

                <FeaturesList />

                <div className='pt-4'>
                    <button
                        type='button'
                        onClick={redirectToPaymentGateway}
                        className={`w-full py-4 px-8 rounded-xl bg-white/90 font-bold text-lg transition-all duration-200 ${
                            isLoading ? 'opacity-50' : 'text-cyan-700 shadow-lg hover:shadow-xl hover:scale-105'
                        }`}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <div className='flex items-center justify-center space-x-2'>
                                <Loader2 className='w-5 h-5 animate-spin' />
                                <span>Processing...</span>
                            </div>
                        ) : (
                            trialPeriodDays > 0 ? `Start Your ${trialPeriodDays}-Day Free Trial` : 'Subscribe Now'
                        )}
                    </button>
                    <p className='mt-4 text-xs text-center text-white/80'>
                        {isTestMode
                            ? 'Test mode — Use card 4242 4242 4242 4242, any future date, any CVC'
                            : trialPeriodDays > 0
                                ? 'No credit card required • Cancel anytime • Secure payment'
                                : 'Cancel anytime • Secure payment powered by Stripe'
                        }
                    </p>
                </div>
            </div>
        </section>
    )
}

// Value-based subscription page (matches Junko's Lovable prototype)
function UsageSubscriptionView({
    plan, setPlan, isLoading, isTestMode, redirectToPaymentGateway, navigate,
}: {
    plan: string
    setPlan: (p: string) => void
    isLoading: boolean
    isTestMode: boolean
    redirectToPaymentGateway: () => void
    navigate: (path: string) => void
}) {
    return (
        <section className='space-y-6 max-w-md mx-auto' key='step-subscription-usage'>
            <div className='text-center'>
                <div className='flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-white/20'>
                    <svg className='w-8 h-8 text-white' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
                    </svg>
                </div>
                <h2 className='mb-3 text-3xl font-bold text-white drop-shadow-lg'>
                    You're off to a great start!
                </h2>
                <p className='text-base text-white/90 leading-relaxed'>
                    You've already taken an important step in supporting your child.
                    BrightPath can continue to support your care journey with clarity and confidence.
                </p>
            </div>

            <div className='space-y-4'>
                {/* Plan selection with radio indicators */}
                <p className='text-sm text-white/80 text-center'>Select a plan:</p>
                <div className='space-y-4'>
                    <button
                        type='button'
                        onClick={() => setPlan('monthly')}
                        className={`relative w-full rounded-2xl bg-white text-gray-900 shadow-lg p-5 text-left transition-all duration-200 border-2 ${
                            plan === 'monthly'
                                ? 'border-cyan-600 ring-2 ring-cyan-300'
                                : 'border-transparent hover:border-gray-200'
                        }`}
                    >
                        <div className='flex items-center justify-between'>
                            <div className='flex items-center gap-3'>
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                    plan === 'monthly' ? 'border-cyan-600' : 'border-gray-300'
                                }`}>
                                    {plan === 'monthly' && <div className='w-2.5 h-2.5 rounded-full bg-cyan-600' />}
                                </div>
                                <h3 className='text-lg font-semibold'>Monthly</h3>
                            </div>
                            <div className='flex items-baseline gap-1'>
                                <span className='text-2xl font-bold'>$4.99</span>
                                <span className='text-sm text-gray-500'>/ month</span>
                            </div>
                        </div>
                    </button>

                    <button
                        type='button'
                        onClick={() => setPlan('annual')}
                        className={`relative w-full rounded-2xl bg-white text-gray-900 shadow-lg p-5 text-left transition-all duration-200 border-2 ${
                            plan === 'annual'
                                ? 'border-cyan-600 ring-2 ring-cyan-300'
                                : 'border-transparent hover:border-gray-200'
                        }`}
                    >
                        <div className='absolute px-3 py-1 text-xs font-bold bg-yellow-400 rounded-full right-4 -top-3 text-cyan-800'>
                            BEST VALUE
                        </div>
                        <div className='flex items-center justify-between'>
                            <div className='flex items-center gap-3'>
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                    plan === 'annual' ? 'border-cyan-600' : 'border-gray-300'
                                }`}>
                                    {plan === 'annual' && <div className='w-2.5 h-2.5 rounded-full bg-cyan-600' />}
                                </div>
                                <div>
                                    <h3 className='text-lg font-semibold'>Annual</h3>
                                    <span className='text-sm line-through text-gray-400'>$59</span>
                                </div>
                            </div>
                            <div className='text-right'>
                                <div className='flex items-baseline gap-1'>
                                    <span className='text-2xl font-bold'>$49</span>
                                    <span className='text-sm text-gray-500'>/ year</span>
                                </div>
                                <p className='text-xs text-green-500 font-medium'>Save $10.88 per year</p>
                            </div>
                        </div>
                    </button>
                </div>

                {/* Features */}
                <div className='p-6 rounded-2xl bg-white/15 backdrop-blur-sm'>
                    <h3 className='mb-1 text-lg font-bold text-center text-white'>BrightPath Starter Plan</h3>
                    <p className='mb-4 text-sm text-center text-white/80'>Everything included in your plan:</p>
                    <div className='space-y-3'>
                        {[
                            'Personalized roadmap that adapts to your child\u2019s development',
                            'Milestone-based checklist & progress tracking',
                            'Daily tips and guidance',
                            'Curated resource library',
                            'Autism-trained chat assistant',
                            'Cancel anytime',
                        ].map((feature) => (
                            <div key={feature} className='flex items-start gap-3 text-white'>
                                <div className='flex items-center justify-center flex-shrink-0 w-5 h-5 mt-0.5 bg-green-400 rounded-full'>
                                    <Check className='w-3 h-3 text-white' />
                                </div>
                                <span className='text-sm leading-snug'>{feature}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* CTA */}
                <div className='pt-4'>
                    <button
                        type='button'
                        onClick={redirectToPaymentGateway}
                        className={`w-full py-4 px-8 rounded-xl bg-white font-bold text-lg transition-all duration-200 ${
                            isLoading ? 'opacity-50' : 'text-cyan-700 shadow-lg hover:shadow-xl hover:scale-105'
                        }`}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <div className='flex items-center justify-center space-x-2'>
                                <Loader2 className='w-5 h-5 animate-spin' />
                                <span>Processing...</span>
                            </div>
                        ) : (
                            'Continue My BrightPath'
                        )}
                    </button>

                    <div className='mt-4 text-center'>
                        <button
                            type='button'
                            onClick={() => navigate('/')}
                            className='text-sm text-white/80 underline underline-offset-2 hover:text-white transition-colors'
                        >
                            Maybe Later
                        </button>
                    </div>

                    <p className='mt-4 text-xs text-center text-white/60'>
                        {isTestMode
                            ? 'Test mode — Use card 4242 4242 4242 4242, any future date, any CVC'
                            : 'Cancel anytime • Secure payment powered by Stripe'
                        }
                    </p>
                </div>
            </div>
        </section>
    )
}

// Shared features list
function FeaturesList() {
    const features = [
        'Personalized roadmaps',
        'AI chat assistant',
        'Curated resource library',
        'Community Support',
        'Progress tracking',
        'Regular updates',
        'Cancel anytime',
    ]

    return (
        <div className='p-6 space-y-4 border bg-white/10 backdrop-blur-sm rounded-2xl border-white/30'>
            <h3 className='text-lg font-semibold text-center text-white'>Everything included in your plan:</h3>
            <div className='grid gap-3 sm:grid-cols-2'>
                {features.map((feature) => (
                    <div key={feature} className='flex items-center space-x-3 text-white'>
                        <div className='flex items-center justify-center flex-shrink-0 w-5 h-5 bg-green-400 rounded-full'>
                            <Check className='w-3 h-3 text-white' />
                        </div>
                        <span className='text-sm'>{feature}</span>
                    </div>
                ))}
            </div>
        </div>
    )
}
